const CACHE_KEY_PREFIX = 'report_cache_';
// Extend expiry from 5 minutes to 1 hour
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour

// Helper function to check if sessionStorage is available
const isSessionStorageAvailable = () => {
  try {
    const testKey = '__test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

const reportCacheUtil = {
  getCacheKey(userId, months, practiceId) {
    return `${CACHE_KEY_PREFIX}${userId}_${months}_${practiceId}`;
  },

  getYoYCacheKey(userId, practiceId) {
    return `${CACHE_KEY_PREFIX}yoy_${userId}_${practiceId}`;
  },

  /**
   * Retrieves data from sessionStorage cache.
   * @param {string} key - The cache key.
   * @returns {any|null} The cached data or null if not found or expired.
   */
  getFromCache: (key) => {
    if (!isSessionStorageAvailable()) {
      console.warn("SessionStorage is not available. Cache operations disabled.");
      return null;
    }
    try {
      const itemStr = sessionStorage.getItem(key);
      if (!itemStr) {
        return null;
      }
      const item = JSON.parse(itemStr);
      const now = new Date();
      // Check if the item has expired
      if (now.getTime() > item.expiry) {
        sessionStorage.removeItem(key); // Remove expired item
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Cache expired for key: ${key}`);
        }
        return null;
      }
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Cache hit for key: ${key}`);
      }
      return item.value; // Return the actual cached value
    } catch (error) {
      console.error(`Error getting cache for key ${key}:`, error);
      return null;
    }
  },

  /**
   * Saves data to sessionStorage cache with a TTL.
   * @param {string} key - The cache key.
   * @param {any} data - The data to cache.
   * @param {number} ttl - Time-to-live in milliseconds.
   */
  saveToCache: (key, data, ttl) => {
    if (!isSessionStorageAvailable()) {
      console.warn("SessionStorage is not available. Cache operations disabled.");
      return;
    }
    try {
      const now = new Date();
      const item = {
        value: data,
        expiry: now.getTime() + ttl, // Calculate expiry timestamp
      };
      sessionStorage.setItem(key, JSON.stringify(item));
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Cache saved for key: ${key} with TTL: ${ttl}ms`);
      }
    } catch (error) {
      console.error(`Error saving cache for key ${key}:`, error);
      // Handle potential storage limit errors
      if (error.name === 'QuotaExceededError') {
        console.warn("SessionStorage quota exceeded. Consider clearing some cache.");
        // Optionally implement a cache eviction strategy here
      }
    }
  },

  /**
   * Removes a specific item from the sessionStorage cache.
   * @param {string} key - The cache key to remove.
   */
  removeFromCache: (key) => {
    if (!isSessionStorageAvailable()) {
      console.warn("SessionStorage is not available. Cache operations disabled.");
      return;
    }
    try {
      sessionStorage.removeItem(key);
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Cache removed for key: ${key}`);
      }
    } catch (error) {
      console.error(`Error removing cache for key ${key}:`, error);
    }
  },

  /**
   * Clears all cache items with keys starting with a specific prefix.
   * @param {string} prefix - The prefix to match keys against.
   */
  clearCacheForPrefix: (prefix) => {
    if (!isSessionStorageAvailable()) {
      console.warn("SessionStorage is not available. Cache operations disabled.");
      return;
    }
    try {
      let removedCount = 0;
      // Iterate over all keys in sessionStorage
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(prefix)) {
          sessionStorage.removeItem(key);
          removedCount++;
        }
      });
      if (removedCount > 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Cleared ${removedCount} cache items with prefix: ${prefix}`);
        }
      }
    } catch (error) {
      console.error(`Error clearing cache for prefix ${prefix}:`, error);
    }
  },

  /**
   * Clears the entire sessionStorage cache managed by this utility (use with caution).
   */
  clearAllCache: () => {
    if (!isSessionStorageAvailable()) {
      console.warn("SessionStorage is not available. Cache operations disabled.");
      return;
    }
    try {
      // A safer approach might be to only clear keys known to be used by the app,
      // e.g., by checking for a common prefix if one is used consistently.
      // For now, clearing all based on the function name.
      const keysToRemove = [];
      Object.keys(sessionStorage).forEach((key) => {
        // Add logic here if you only want to clear specific app-related keys
        // For example: if (key.startsWith('app_cache_')) keysToRemove.push(key);
        keysToRemove.push(key); // Currently removes all keys
      });
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Cleared all ${keysToRemove.length} items from sessionStorage cache.`);
      }
    } catch (error) {
      console.error("Error clearing all cache:", error);
    }
  }
};

// Export the utility object
export { reportCacheUtil };